import React, { useState, useEffect } from "react";
import { Button, Input, Tabs, Row, Col, Select, Modal, Space, Typography } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import { deleteMedication, fetchMedications } from "../../../../api/healthcare/MedicationsApi";
import CreateMedicationModal from "./submodals/CreateMedicationModal";
import { Patient } from "../../../../types/healthcare/PatientTypes";
import { Medication, defaultMedication } from "../../../../types/healthcare/MedicationTypes";
import { useNotifications } from "../../../../context/NotificationContext";
import { MEDICATION_CATEGORIES } from "../../../../types/healthcare/MedicationCatalogTypes";
import StatusTag from "../../../../components/shared/StatusTag";
import { DataTable } from "../../../../components/shared";

const { Text } = Typography;

interface MedicationsTabProps {
	patient: Patient;
	handleAutoSave?: () => void;
}

const MedicationsTab: React.FC<MedicationsTabProps> = ({ patient }) => {
	const [medications, setMedications] = useState<Medication[]>([]);
	const [activeMedications, setActiveMedications] = useState<Medication[]>([]);
	const [inactiveMedications, setInactiveMedications] = useState<Medication[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [dialogVisible, setDialogVisible] = useState<boolean>(false);
	const [currentMedication, setCurrentMedication] = useState<Medication>(defaultMedication);
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [activeTab, setActiveTab] = useState<string>("all");
	const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const { showError, showSuccess } = useNotifications();


	useEffect(() => {
		if (patient?.id) loadMedications();
	}, [patient?.id]);

	const loadMedications = async () => {
		if (!patient?.id) return;
		setLoading(true);
		try {
			const { data } = await fetchMedications(patient.id);
			setMedications(data);
			setActiveMedications(data.filter((m) => m.active));
			setInactiveMedications(data.filter((m) => !m.active));
		} catch {
			showError("Failed to load medications");
		} finally {
			setLoading(false);
		}
	};

	const openNewMedicationDialog = () => {
		setCurrentMedication({ ...defaultMedication, patient_id: patient.id });
		setIsEditing(false);
		setDialogVisible(true);
	};

	const openEditMedicationDialog = (medication: Medication) => {
		setCurrentMedication({ ...medication, start_date: medication.start_date ? new Date(medication.start_date) : new Date(), end_date: medication.end_date ? new Date(medication.end_date) : undefined });
		setIsEditing(true);
		setDialogVisible(true);
	};

	const handleDelete = async (medication: Medication) => {
		Modal.confirm({
			title: "Confirm Deletion",
			content: "Are you sure you want to delete this medication?",
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					await deleteMedication(medication.id);
					showSuccess("Medication deleted successfully");
					loadMedications();
				} catch {
					showError("Failed to delete medication");
				}
			},
		});
	};

	const getFilteredMedications = (list: Medication[]) => {
		let filtered = [...list];
		if (searchTerm) {
			const q = searchTerm.toLowerCase();
			filtered = filtered.filter((m) => m.medication_name?.toLowerCase().includes(q) || m.prescribed_by?.toLowerCase().includes(q) || m.dosage?.toLowerCase().includes(q));
		}
		if (selectedCategory) filtered = filtered.filter((m) => m.medication_name?.toLowerCase().includes(selectedCategory.toLowerCase()));
		return filtered;
	};


	const columns: any[] = [
		{ title: "Medication", dataIndex: "medication_name", key: "medication_name", sorter: (a: Medication, b: Medication) => (a.medication_name || "").localeCompare(b.medication_name || "") },
		{ title: "Dosage", dataIndex: "dosage", key: "dosage" },
		{ title: "Frequency", dataIndex: "frequency", key: "frequency" },
		{ title: "Start Date", dataIndex: "start_date", key: "start_date", render: (d: Date) => (d ? new Date(d).toLocaleDateString() : "—") },
		{ title: "End Date", dataIndex: "end_date", key: "end_date", render: (d: Date) => (d ? new Date(d).toLocaleDateString() : "—") },
		{ title: "Status", key: "active", render: (record: Medication) => <StatusTag status={record.active ? "active" : "inactive"} /> },
		{
			title: "Actions",
			key: "actions",
			fixed: "right" as const,
			render: (record: Medication) => (
				<Space>
					<Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
				</Space>
			),
		},
	];

	const tableProps = {
		columns,
		loading,
		scrollX: "max-content" as const,
		onRowClick: (record: Medication) => openEditMedicationDialog(record),
		pagination: { pageSize: 30, showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} of ${total} medications` },
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
				<Button type="primary" icon={<PlusOutlined />} onClick={openNewMedicationDialog}>
					Add Medication
				</Button>
				<Space>
					<Input placeholder="Search medications" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} prefix={<SearchOutlined />} style={{ width: 250 }} allowClear />
					<Select style={{ width: 200 }} value={selectedCategory} onChange={setSelectedCategory} placeholder="Filter by category" options={MEDICATION_CATEGORIES} allowClear />
				</Space>
			</div>

			<>
				<Tabs
					activeKey={activeTab}
					onChange={setActiveTab}
					items={[
						{ key: "all", label: "All Medications", children: <DataTable rowKey="id" dataSource={getFilteredMedications(medications)} {...tableProps} /> },
						{ key: "active", label: "Active", children: <DataTable rowKey="id" dataSource={getFilteredMedications(activeMedications)} {...tableProps} /> },
						{ key: "inactive", label: "Inactive", children: <DataTable rowKey="id" dataSource={getFilteredMedications(inactiveMedications)} {...tableProps} /> },
					]}
				/>
			</>

			{dialogVisible && <CreateMedicationModal currentMedication={currentMedication} setCurrentMedication={setCurrentMedication} isEditing={isEditing} isVisible={dialogVisible} onClose={() => setDialogVisible(false)} isConsult={false} onSaved={() => loadMedications()} />}
		</div>
	);
};

export default MedicationsTab;
